import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Activity, Clock, Wifi, WifiOff, Shield, Server,
  AlertTriangle, CheckCircle, XCircle, Settings, Wrench,
  RefreshCw, Monitor, HardDrive, Router, Camera,
  Cpu, BarChart3, TrendingUp, Zap, Video, ExternalLink, Play, Pencil,
  Thermometer, Database, Fan, MemoryStick, HardDrive as HardDriveIcon, BatteryCharging,
  Search, Eye, EyeOff
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import fetchWithTenant from '../utils/fetchWithTenant';
import WebRTCPlayer from '../components/WebRTCPlayer';
import JanusPlayer from '../components/JanusPlayer';
import '../styles/v2/pages/md-device-detail.css';
import useAuthStore from '../stores/authStore';

const DEVICE_ICONS = {
  CAMERA: Camera, SERVER: Server, ROUTER: Router, SWITCH: HardDrive,
  NVR: Monitor, WORKSTATION: Monitor, ACCESS_POINT: Wifi,
  DOOR_CONTROLLER: Shield, INTERCOM: Activity, UNKNOWN: Cpu
};

const STATUS_CONFIG = {
  ONLINE: { color: '#00c853', bg: '#00c85315', label: 'Online', Icon: CheckCircle },
  OFFLINE: { color: '#ff1744', bg: '#ff174415', label: 'Offline', Icon: XCircle },
  DEGRADED: { color: '#ffaa00', bg: '#ffaa0015', label: 'Degraded', Icon: AlertTriangle },
  MAINTENANCE: { color: '#448aff', bg: '#448aff15', label: 'Maintenance', Icon: Wrench },
  UNKNOWN: { color: '#78909c', bg: '#78909c15', label: 'Unknown', Icon: Activity }
};

export default function MdMonitoredDeviceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { tenantVersion } = useAuthStore();
  const [device, setDevice] = useState(null);
  const [polls, setPolls] = useState([]);
  const [uptimeStats, setUptimeStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pollTimeRange, setPollTimeRange] = useState(24);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [snapshotUrl, setSnapshotUrl] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [editName, setEditName] = useState('');
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [snapshotError, setSnapshotError] = useState(false);
  const [showLiveModal, setShowLiveModal] = useState(false);
  const [snmpMetrics, setSnmpMetrics] = useState([]);
  const [janusStreamId, setJanusStreamId] = useState(null);
  const [streamLoading, setStreamLoading] = useState(false);
  const [streamError, setStreamError] = useState(null);

  // OS Inventory tab (WMI-monitored devices only, keyed off discoveredDevice.id)
  const [osInventory, setOsInventory] = useState(null);
  const [osInventoryError, setOsInventoryError] = useState(null);
  const [osHealth, setOsHealth] = useState(null);
  const [watchedServices, setWatchedServices] = useState([]);
  const [inventoryRefreshing, setInventoryRefreshing] = useState(false);
  const [softwareSearch, setSoftwareSearch] = useState('');
  const [watchBusyName, setWatchBusyName] = useState(null);

  const fetchDevice = useCallback(async () => {
    try {
      const res = await fetchWithTenant('/api/md/monitored/' + id);
      const data = await res.json();
      if (data.device) setDevice(data.device);
      else if (data.id) setDevice(data);
    } catch (err) {
      console.error('Failed to fetch device:', err);
    }
  }, [id]);

  const fetchPolls = useCallback(async () => {
    try {
      const res = await fetchWithTenant('/api/md/monitored/' + id + '/polls?hours=' + pollTimeRange);
      const data = await res.json();
      setPolls(data.polls || []);
    } catch (err) {
      console.error('Failed to fetch polls:', err);
    }
  }, [id, pollTimeRange]);

  const fetchSnmpMetrics = useCallback(async () => {
    try {
      const res = await fetchWithTenant('/api/md/monitored/' + id + '/snmp-metrics?hours=' + pollTimeRange);
      if (res.ok) { const data = await res.json(); setSnmpMetrics(data.metrics || []); }
    } catch (err) { console.error('Failed to fetch SNMP metrics:', err); }
  }, [id, pollTimeRange]);

  const fetchUptime = useCallback(async () => {
    try {
      const res = await fetchWithTenant('/api/md/monitored/' + id + '/uptime');
      const data = await res.json();
      setUptimeStats(data.stats || null);
    } catch (err) {
      console.error('Failed to fetch uptime:', err);
    }
  }, [id]);

  const discoveredDeviceId = device?.discoveredDevice?.id || null;

  const fetchOsInventory = useCallback(async () => {
    if (!discoveredDeviceId) return;
    try {
      const res = await fetchWithTenant('/api/md/devices/' + discoveredDeviceId + '/os-inventory');
      if (res.status === 404) {
        setOsInventory(null);
        setOsInventoryError('No inventory collected yet');
        return;
      }
      if (!res.ok) throw new Error('Failed to load inventory');
      setOsInventory(await res.json());
      setOsInventoryError(null);
    } catch (err) {
      setOsInventoryError(err.message);
    }
  }, [discoveredDeviceId]);

  const fetchOsHealth = useCallback(async () => {
    if (!discoveredDeviceId) return;
    try {
      const res = await fetchWithTenant('/api/md/devices/' + discoveredDeviceId + '/os-health?hours=24');
      if (res.ok) setOsHealth(await res.json());
    } catch (err) {
      console.error('Failed to fetch OS health:', err);
    }
  }, [discoveredDeviceId]);

  const fetchWatchedServices = useCallback(async () => {
    if (!discoveredDeviceId) return;
    try {
      const res = await fetchWithTenant('/api/md/devices/' + discoveredDeviceId + '/watched-services');
      if (res.ok) {
        const data = await res.json();
        setWatchedServices(data.services || []);
      }
    } catch (err) {
      console.error('Failed to fetch watched services:', err);
    }
  }, [discoveredDeviceId]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchDevice(), fetchPolls(), fetchUptime(), fetchSnmpMetrics()])
      .finally(() => setLoading(false));
  }, [fetchDevice, fetchPolls, fetchUptime, fetchSnmpMetrics]);

  useEffect(() => { fetchPolls(); fetchSnmpMetrics(); }, [pollTimeRange, fetchPolls, fetchSnmpMetrics]);

  // Lazy-load the OS Inventory tab's data only once it's actually opened.
  useEffect(() => {
    if (activeTab === 'inventory' && discoveredDeviceId && !osInventory && !osInventoryError) {
      fetchOsInventory();
      fetchOsHealth();
      fetchWatchedServices();
    }
  }, [activeTab, discoveredDeviceId, osInventory, osInventoryError, fetchOsInventory, fetchOsHealth, fetchWatchedServices]);

  // Fetch camera snapshot for WAVE_SYNC cameras
  useEffect(() => {
    if (!device) return;
    const isCamera = device.deviceType === 'CAMERA' || device.deviceType === 'NVR';
    const isWave = device.source === 'INTEGRATION';
    if (isCamera && isWave && device.externalId) {
      setSnapshotLoading(true);
      setSnapshotError(false);
      fetchWithTenant('/api/vms/cameras/' + device.externalId + '/thumbnail')
        .then(res => {
          if (!res.ok) throw new Error('No thumbnail');
          return res.blob();
        })
        .then(blob => {
          setSnapshotUrl(URL.createObjectURL(blob));
          setSnapshotLoading(false);
        })
        .catch(() => {
          setSnapshotError(true);
          setSnapshotLoading(false);
        });
    }
  }, [device]);

  const handleOpenLiveView = async () => {
    setShowLiveModal(true);
    setStreamError(null);
    if (device.source === 'INTEGRATION' && device.externalId) return;
    setStreamLoading(true);
    try {
      const res = await fetchWithTenant('/api/md/monitored/' + device.id + '/stream', { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create stream');
      setJanusStreamId(data.streamId);
    } catch (err) {
      setStreamError(err.message);
    } finally {
      setStreamLoading(false);
    }
  };

  const handleCloseLiveView = () => {
    setShowLiveModal(false);
    if (janusStreamId && device.source !== 'INTEGRATION') {
      fetchWithTenant('/api/md/monitored/' + device.id + '/stream', { method: 'DELETE' }).catch(() => {});
      setJanusStreamId(null);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchDevice(), fetchPolls(), fetchUptime(), fetchSnmpMetrics()]);
    setRefreshing(false);
  };

  const handleRefreshInventory = async () => {
    if (!discoveredDeviceId) return;
    setInventoryRefreshing(true);
    try {
      const res = await fetchWithTenant('/api/md/devices/' + discoveredDeviceId + '/os-inventory/refresh', { method: 'POST' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Refresh failed');
      }
      // Agent may take a few seconds to complete the poll and ingest --
      // give it time before re-fetching rather than racing it.
      setTimeout(async () => {
        await fetchOsInventory();
        setInventoryRefreshing(false);
      }, 5000);
    } catch (err) {
      console.error('Failed to refresh inventory:', err);
      setInventoryRefreshing(false);
    }
  };

  const findWatch = (serviceName) => watchedServices.find(w => w.serviceName === serviceName);

  const handleToggleWatch = async (service) => {
    if (!discoveredDeviceId) return;
    setWatchBusyName(service.name);
    try {
      const existing = findWatch(service.name);
      if (existing) {
        const res = await fetchWithTenant('/api/md/devices/' + discoveredDeviceId + '/watched-services/' + existing.id, { method: 'DELETE' });
        if (res.ok) setWatchedServices(ws => ws.filter(w => w.id !== existing.id));
      } else {
        const res = await fetchWithTenant('/api/md/devices/' + discoveredDeviceId + '/watched-services', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ serviceName: service.name, displayName: service.displayName || null }),
        });
        if (res.ok) {
          const data = await res.json();
          setWatchedServices(ws => [...ws, data.watched]);
        }
      }
    } catch (err) {
      console.error('Failed to toggle watched service:', err);
    } finally {
      setWatchBusyName(null);
    }
  };

  const handleSaveName = async () => {
    if (!editName.trim() || editName === device.name) { setEditingName(false); return; }
    try {
      const res = await fetchWithTenant('/api/md/monitored/' + id, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editName.trim() }),
      });
      if (res.ok) {
        setDevice(d => ({ ...d, name: editName.trim() }));
        setEditingName(false);
      }
    } catch (err) { console.error('Failed to save name:', err); }
  };

  const chartData = polls.map(p => ({
    time: new Date(p.checkedAt).getTime(),
    latency: p.latencyMs || 0,
    success: p.success ? 1 : 0
  }));

  const validLatencies = polls.filter(p => p.success && p.latencyMs != null);
  const avgLatency = validLatencies.length > 0
    ? (validLatencies.reduce((s, p) => s + p.latencyMs, 0) / validLatencies.length).toFixed(1)
    : null;
  const maxLatency = validLatencies.length > 0
    ? Math.max(...validLatencies.map(p => p.latencyMs)).toFixed(1)
    : null;
  const minLatency = validLatencies.length > 0
    ? Math.min(...validLatencies.map(p => p.latencyMs)).toFixed(1)
    : null;
  const successRate = polls.length > 0
    ? ((polls.filter(p => p.success).length / polls.length) * 100).toFixed(1)
    : null;

  const osHealthSnapshots = osHealth?.snapshots || [];
  const osHealthChartData = osHealthSnapshots.map(s => ({
    time: new Date(s.timestamp).getTime(),
    cpu: s.cpuPercent || 0,
    memory: s.memoryPercent || 0,
  }));
  const latestOsHealth = osHealthSnapshots.length ? osHealthSnapshots[osHealthSnapshots.length - 1] : null;
  const latestCpu = latestOsHealth?.cpuPercent ?? null;
  const latestMem = latestOsHealth?.memoryPercent ?? null;
  const latestDiskMax = (osHealth?.latestDisks || []).length
    ? Math.max(...osHealth.latestDisks.map(d => d.percent || 0))
    : null;

  const filteredSoftware = (osInventory?.software || []).filter(sw => {
    if (!softwareSearch) return true;
    const q = softwareSearch.toLowerCase();
    return (sw.name || '').toLowerCase().includes(q) || (sw.publisher || '').toLowerCase().includes(q);
  });

  const svcStatusColor = (s) => (s || '').toLowerCase() === 'running' ? '#00c853' : '#78909c';
  const svcStatusStyle = (s) => ({ color: svcStatusColor(s), borderColor: svcStatusColor(s) + '40', background: svcStatusColor(s) + '15' });

  if (loading) {
    return (
      <div className="mdd-page">
        <div className="mdd-loading">
          <RefreshCw size={24} className="spin" />
          <span>Loading device details...</span>
        </div>
      </div>
    );
  }

  if (!device) {
    return (
      <div className="mdd-page">
        <div className="mdd-not-found">
          <XCircle size={48} />
          <h2>Device not found</h2>
          <button className="btn btn-secondary" onClick={() => navigate('/monitoring/devices')}>
            <ArrowLeft size={16} /> Back to Devices
          </button>
        </div>
      </div>
    );
  }

  const status = STATUS_CONFIG[device.status] || STATUS_CONFIG.UNKNOWN;
  const StatusIcon = status.Icon;
  const DeviceIcon = DEVICE_ICONS[device.deviceType] || DEVICE_ICONS.UNKNOWN;

  return (
    <div className="mdd-page">
      <div className="mdd-header">
        <div className="mdd-header-left">
          <button className="mdd-back-btn" onClick={() => navigate('/monitoring/devices')}>
            <ArrowLeft size={18} />
          </button>
          <div className="mdd-device-icon-wrap" style={{ borderColor: status.color + '60' }}>
            <DeviceIcon size={22} style={{ color: status.color }} />
          </div>
          <div className="mdd-header-info">
            {editingName ? (
              <input className="mdd-name-input" value={editName} onChange={(e) => setEditName(e.target.value)}
                onBlur={handleSaveName} onKeyDown={(e) => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') setEditingName(false); }}
                autoFocus />
            ) : (
              <div className="mdd-name-edit" onClick={() => { setEditName(device.name); setEditingName(true); }}>
                <h1 className="mdd-device-name">{device.name}</h1>
                <Pencil size={14} className="mdd-edit-icon" />
              </div>
            )}
            <div className="mdd-device-meta">
              <span className="mdd-ip">{device.ipAddress}</span>
              {device.hostname && <span className="mdd-hostname">{device.hostname}</span>}
              <span className="mdd-profile">{device.monitoringProfile}</span>
              <span className="mdd-status-pill" style={{ color: status.color, borderColor: status.color + '40', background: status.bg }}>
                <StatusIcon size={12} /> {status.label}
              </span>
            </div>
          </div>
        </div>
        <div className="mdd-header-actions">
          {device.discoveredDevice?.agentId && device.ipAddress && (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => navigate(
                `/monitoring/manage/${device.discoveredDevice.agentId}/${device.ipAddress}/80?name=${encodeURIComponent(device.name)}&return=/monitoring/devices/${device.id}`
              )}
            >
              <ExternalLink size={14} /> Manage Device
            </button>
          )}
          <button className="btn btn-secondary btn-sm" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw size={14} className={refreshing ? 'spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      <div className="mdd-stats-row">
        <div className="mdd-stat-card">
          <div className="mdd-stat-icon" style={{ color: '#00d4aa' }}><TrendingUp size={18} /></div>
          <div className="mdd-stat-body">
            <span className="mdd-stat-value">{avgLatency || '\u2014'}<small>ms</small></span>
            <span className="mdd-stat-label">Avg Latency</span>
          </div>
        </div>
        <div className="mdd-stat-card">
          <div className="mdd-stat-icon" style={{ color: '#ffaa00' }}><Zap size={18} /></div>
          <div className="mdd-stat-body">
            <span className="mdd-stat-value">{maxLatency || '\u2014'}<small>ms</small></span>
            <span className="mdd-stat-label">Max Latency</span>
          </div>
        </div>
        <div className="mdd-stat-card">
          <div className="mdd-stat-icon" style={{ color: '#448aff' }}><BarChart3 size={18} /></div>
          <div className="mdd-stat-body">
            <span className="mdd-stat-value">{minLatency || '\u2014'}<small>ms</small></span>
            <span className="mdd-stat-label">Min Latency</span>
          </div>
        </div>
        <div className="mdd-stat-card">
          <div className="mdd-stat-icon" style={{ color: successRate >= 99 ? '#00c853' : successRate >= 95 ? '#ffaa00' : '#ff1744' }}>
            <CheckCircle size={18} />
          </div>
          <div className="mdd-stat-body">
            <span className="mdd-stat-value">{successRate || '\u2014'}<small>%</small></span>
            <span className="mdd-stat-label">Success Rate</span>
          </div>
        </div>
      </div>

      {uptimeStats && (
        <div className="mdd-uptime-row">
          <h3 className="mdd-section-title">Uptime</h3>
          <div className="mdd-uptime-bars">
            {['1h', '24h', '7d', '30d'].map(period => {
              const s = uptimeStats[period];
              const pct = s && s.uptime ? parseFloat(s.uptime) : 0;
              const color = pct >= 99.5 ? '#00c853' : pct >= 95 ? '#ffaa00' : pct > 0 ? '#ff1744' : '#555';
              return (
                <div key={period} className="mdd-uptime-item">
                  <div className="mdd-uptime-label">{period}</div>
                  <div className="mdd-uptime-bar-track">
                    <div className="mdd-uptime-bar-fill" style={{ width: pct + '%', background: color }} />
                  </div>
                  <div className="mdd-uptime-pct" style={{ color }}>{s && s.uptime ? s.uptime + '%' : '\u2014'}</div>
                  <div className="mdd-uptime-count">{s ? s.successful : 0}/{s ? s.total : 0} checks</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mdd-tabs">
        {[...['overview', 'polls', 'alerts', 'info'], ...(device.discoveredDevice?.bmcData?.supported ? ['hardware'] : []), ...(discoveredDeviceId ? ['inventory'] : [])].map(tab => (
          <button key={tab} className={'mdd-tab' + (activeTab === tab ? ' active' : '')} onClick={() => setActiveTab(tab)}>
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      <div className="mdd-tab-content">
        {activeTab === 'overview' && (
          <div className="mdd-overview">
            {/* Camera Preview Section */}
            {(device.deviceType === 'CAMERA' || device.deviceType === 'NVR') && (
              <div className="mdd-camera-card">
                <div className="mdd-camera-preview">
                  {snapshotUrl ? (
                    <img src={snapshotUrl} alt={device.name + ' snapshot'} className="mdd-snapshot-img" />
                  ) : snapshotLoading ? (
                    <div className="mdd-snapshot-placeholder"><RefreshCw size={24} className="spin" /><span>Loading snapshot...</span></div>
                  ) : (
                    <div className="mdd-snapshot-placeholder"><Video size={32} /><span>{snapshotError ? 'Snapshot unavailable' : 'No preview available'}</span></div>
                  )}
                </div>
                <div className="mdd-camera-info">
                  <h3><Video size={16} /> Camera Preview</h3>
                  {(device.externalId || device.source === 'DISCOVERY') && (
                    <div className="mdd-camera-actions">
                      <button className="btn btn-primary btn-sm" onClick={handleOpenLiveView}>
                        <Play size={14} /> Open Live View
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={() => {
                        setSnapshotLoading(true);
                        setSnapshotError(false);
                        fetchWithTenant('/api/vms/cameras/' + device.externalId + '/thumbnail')
                          .then(res => res.ok ? res.blob() : Promise.reject())
                          .then(blob => { setSnapshotUrl(URL.createObjectURL(blob)); setSnapshotLoading(false); })
                          .catch(() => { setSnapshotError(true); setSnapshotLoading(false); });
                      }}>
                        <RefreshCw size={14} /> Refresh Snapshot
                      </button>
                    </div>
                  )}
                  <div className="mdd-camera-details">
                    {device.externalId && <div className="mdd-cam-detail"><span>Camera ID</span><code>{device.externalId}</code></div>}
                    {device.discoveredDevice?.rtspStreams && (
                      <div className="mdd-cam-detail">
                        <span>RTSP Streams</span>
                        <div className="mdd-rtsp-list">
                          {(typeof device.discoveredDevice.rtspStreams === 'string' ? JSON.parse(device.discoveredDevice.rtspStreams) : device.discoveredDevice.rtspStreams)?.streamUris?.map((uri, i) => (
                            <code key={i} className="mdd-rtsp-uri">{uri}</code>
                          ))}
                        </div>
                      </div>
                    )}
                    {device.discoveredDevice?.rtspStreams && (() => {
                      const rs = typeof device.discoveredDevice.rtspStreams === 'string' ? JSON.parse(device.discoveredDevice.rtspStreams) : device.discoveredDevice.rtspStreams;
                      return rs?.codecs?.length > 0 ? (
                        <div className="mdd-cam-detail"><span>Codecs</span><span>{rs.codecs.join(', ')}</span></div>
                      ) : null;
                    })()}
                    {device.discoveredDevice?.rtspStreams && (() => {
                      const rs = typeof device.discoveredDevice.rtspStreams === 'string' ? JSON.parse(device.discoveredDevice.rtspStreams) : device.discoveredDevice.rtspStreams;
                      return rs?.resolution ? (
                        <div className="mdd-cam-detail"><span>Resolution</span><span>{rs.resolution}</span></div>
                      ) : null;
                    })()}
                  </div>
                </div>
              </div>
            )}

            <div className="mdd-chart-card">
              <div className="mdd-chart-header">
                <h3>Latency Over Time</h3>
                <div className="mdd-time-selector">
                  {[1, 6, 12, 24, 48, 168].map(h => (
                    <button key={h} className={'mdd-time-btn' + (pollTimeRange === h ? ' active' : '')} onClick={() => setPollTimeRange(h)}>
                      {h < 24 ? h + 'h' : h === 24 ? '1d' : h === 48 ? '2d' : '7d'}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mdd-chart-body">
                {chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="latencyGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#00d4aa" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#00d4aa" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#2a2f3a" />
                      <XAxis dataKey="time" type="number" domain={['dataMin', 'dataMax']}
                        tickFormatter={(t) => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        stroke="#666" tick={{ fontSize: 11 }} />
                      <YAxis stroke="#666" tick={{ fontSize: 11 }} unit="ms" />
                      <Tooltip contentStyle={{ background: '#1a1f2e', border: '1px solid #333', borderRadius: 8 }}
                        labelFormatter={(t) => new Date(t).toLocaleString()}
                        formatter={(v) => [v.toFixed(1) + ' ms', 'Latency']} />
                      <ReferenceLine y={parseFloat(avgLatency) || 0} stroke="#ffaa00" strokeDasharray="5 5" opacity={0.5} />
                      <Area type="monotone" dataKey="latency" stroke="#00d4aa" fill="url(#latencyGrad)" strokeWidth={2} dot={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="mdd-no-data">No poll data for this time range</div>
                )}
              </div>
            </div>

            <div className="mdd-chart-card">
              <div className="mdd-chart-header">
                <h3>System Uptime</h3>
                {device.metadata?.snmpHealth && (
                  <span className="mdd-snmp-poll-time">
                    Current: {device.metadata.snmpHealth.sysUpTimeStr || "\u2014"}
                    {device.metadata.snmpHealth.rebooted && <span className="mdd-reboot-badge">Reboot Detected</span>}
                  </span>
                )}
              </div>
              <div className="mdd-chart-body">
                {snmpMetrics.length > 1 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={snmpMetrics.map(m => ({
                      time: new Date(m.timestamp).getTime(),
                      uptime: parseFloat((m.value / 3600).toFixed(2)),
                      rebooted: m.metadata?.rebooted || false
                    }))} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="uptimeGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#448aff" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#448aff" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#2a2f3a" />
                      <XAxis dataKey="time" type="number" domain={['dataMin', 'dataMax']}
                        tickFormatter={(t) => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        stroke="#666" tick={{ fontSize: 11 }} />
                      <YAxis stroke="#666" tick={{ fontSize: 11 }} unit="h" />
                      <Tooltip contentStyle={{ background: '#1a1f2e', border: '1px solid #333', borderRadius: 8 }}
                        labelFormatter={(t) => new Date(t).toLocaleString()}
                        formatter={(v) => [v + ' hours', 'Uptime']} />
                      <Area type="monotone" dataKey="uptime" stroke="#448aff" fill="url(#uptimeGrad)" strokeWidth={2} dot={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="mdd-no-data">{device.monitoringProfile === 'SNMP_POLL' ? 'Collecting uptime data...' : 'System uptime requires SNMP polling'}</div>
                )}
              </div>
              {/* Interface Summary */}
              {device.metadata?.snmpHealth?.interfaces && device.metadata.snmpHealth.interfaces.length > 0 && (
                <div className="mdd-iface-summary">
                  {device.metadata.snmpHealth.interfaces.filter(i => i.speed > 0).map((iface, idx) => (
                    <div key={idx} className={"mdd-iface-pill " + (iface.operStatus === 1 ? "up" : "down")}>
                      <span className="mdd-iface-dot" />
                      {iface.description}
                      <span className="mdd-iface-speed">{iface.speed >= 1e9 ? (iface.speed / 1e9).toFixed(0) + 'G' : (iface.speed / 1e6).toFixed(0) + 'M'}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'polls' && (
          <div className="mdd-polls">
            <div className="mdd-table-wrap">
              <table className="mdd-table">
                <thead>
                  <tr><th>Time</th><th>Status</th><th>Latency</th><th>HTTP Code</th><th>Error</th></tr>
                </thead>
                <tbody>
                  {(device.pollHistory || []).map((p, i) => (
                    <tr key={i} className={p.success ? '' : 'poll-fail'}>
                      <td>{new Date(p.checkedAt).toLocaleString()}</td>
                      <td><span className={'mdd-poll-badge ' + (p.success ? 'success' : 'fail')}>{p.success ? 'OK' : 'FAIL'}</span></td>
                      <td>{p.latencyMs != null ? p.latencyMs + ' ms' : '\u2014'}</td>
                      <td>{p.statusCode || '\u2014'}</td>
                      <td className="mdd-error-cell">{p.error || '\u2014'}</td>
                    </tr>
                  ))}
                  {(!device.pollHistory || device.pollHistory.length === 0) && (
                    <tr><td colSpan="5" className="mdd-no-data">No poll results yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'alerts' && (
          <div className="mdd-alerts">
            {(device.alerts || []).length === 0 ? (
              <div className="mdd-empty-state">
                <CheckCircle size={40} style={{ color: '#00c853' }} />
                <p>No alerts for this device</p>
              </div>
            ) : (
              <div className="mdd-alert-list">
                {device.alerts.map(alert => (
                  <div key={alert.id} className={'mdd-alert-card ' + (alert.severity || 'warning')}>
                    <div className="mdd-alert-header">
                      <AlertTriangle size={16} />
                      <span className="mdd-alert-type">{alert.type || 'Alert'}</span>
                      <span className="mdd-alert-time">{new Date(alert.triggeredAt).toLocaleString()}</span>
                    </div>
                    <p className="mdd-alert-msg">{alert.message}</p>
                    {alert.resolvedAt && (
                      <span className="mdd-alert-resolved">Resolved: {new Date(alert.resolvedAt).toLocaleString()}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'info' && (
          <div className="mdd-info">
            <div className="mdd-info-grid">
              <div className="mdd-info-section">
                <h3>Network Identity</h3>
                <dl>
                  <dt>IP Address</dt><dd>{device.ipAddress}</dd>
                  <dt>MAC Address</dt><dd>{device.macAddress || '\u2014'}</dd>
                  <dt>Hostname</dt><dd>{device.hostname || '\u2014'}</dd>
                  <dt>Device Type</dt><dd>{device.deviceType}</dd>
                  <dt>Manufacturer</dt><dd>{device.manufacturer || '\u2014'}</dd>
                  <dt>Model</dt><dd>{device.model || '\u2014'}</dd>
                  <dt>Web Portal</dt><dd>{(device.discoveredDevice?.webInterface || device.metadata?.webInterface) ? <a href={device.discoveredDevice?.webInterface || device.metadata?.webInterface} target="_blank" rel="noopener noreferrer" className="mdd-web-link"><ExternalLink size={12} /> {device.discoveredDevice?.webInterface || device.metadata?.webInterface}</a> : <a href={'http://' + device.ipAddress} target="_blank" rel="noopener noreferrer" className="mdd-web-link"><ExternalLink size={12} /> http://{device.ipAddress}</a>}</dd>
                </dl>
              </div>
              <div className="mdd-info-section">
                <h3>Monitoring Config</h3>
                <dl>
                  <dt>Profile</dt><dd>{device.monitoringProfile}</dd>
                  <dt>Poll Interval</dt><dd>{device.pollIntervalSeconds}s</dd>
                  <dt>Alert Threshold</dt><dd>{device.alertThresholdSeconds}s</dd>
                  <dt>HTTP Endpoint</dt><dd>{device.httpEndpoint || '\u2014'}</dd>
                  <dt>Expected Status</dt><dd>{device.httpExpectedStatus || '\u2014'}</dd>
                  <dt>Monitoring</dt><dd>{device.monitoringEnabled ? 'Enabled' : 'Disabled'}</dd>
                </dl>
              </div>
              <div className="mdd-info-section">
                <h3>Current State</h3>
                <dl>
                  <dt>Status</dt><dd style={{ color: status.color }}>{status.label}</dd>
                  <dt>Last Check</dt><dd>{device.lastCheckAt ? new Date(device.lastCheckAt).toLocaleString() : '\u2014'}</dd>
                  <dt>Last Online</dt><dd>{device.lastOnlineAt ? new Date(device.lastOnlineAt).toLocaleString() : '\u2014'}</dd>
                  <dt>Last Offline</dt><dd>{device.lastOfflineAt ? new Date(device.lastOfflineAt).toLocaleString() : '\u2014'}</dd>
                  <dt>Last Latency</dt><dd>{device.lastLatencyMs != null ? device.lastLatencyMs + ' ms' : '\u2014'}</dd>
                  <dt>Consecutive Failures</dt><dd>{device.consecutiveFailures}</dd>
                  <dt>Health Score</dt><dd>{device.healthScore != null ? device.healthScore : '\u2014'}</dd>
                </dl>
              </div>
              <div className="mdd-info-section">
                <h3>Source & Linking</h3>
                <dl>
                  <dt>Source</dt><dd>{device.source}</dd>
                  <dt>Integration</dt><dd>{device.integration ? device.integration.name : '\u2014'}</dd>
                  <dt>Group</dt><dd>{device.group || '\u2014'}</dd>
                  <dt>Tags</dt><dd>{device.tags && device.tags.length > 0 ? device.tags.join(', ') : '\u2014'}</dd>
                  <dt>Created</dt><dd>{new Date(device.createdAt).toLocaleString()}</dd>
                  {device.maintenanceUntil && (
                    <><dt>Maintenance Until</dt><dd>{new Date(device.maintenanceUntil).toLocaleString()}</dd></>
                  )}
                </dl>
              </div>

              {/* SNMP Enrichment Data */}
              {(() => {
                const snmp = device.metadata || device.discoveredDevice?.snmpData;
                if (!snmp?.supported) return null;
                return (
                  <div className="mdd-info-section mdd-info-snmp">
                    <h3>SNMP Discovery</h3>
                    <dl>
                      <dt>SNMP Version</dt><dd>{snmp.version}</dd>
                      <dt>System Name</dt><dd>{snmp.sysName || '\u2014'}</dd>
                      <dt>System Description</dt><dd className="mdd-snmp-descr">{snmp.sysDescr || '\u2014'}</dd>
                      <dt>Enterprise</dt><dd>{snmp.enterprise || '\u2014'}</dd>
                      <dt>Device Model</dt><dd>{snmp.deviceModel || '\u2014'}</dd>
                      <dt>Firmware</dt><dd>{device.firmware || snmp.firmwareVer || '\u2014'}</dd>
                      <dt>OS / Platform</dt><dd>{snmp.osInfo || '\u2014'}</dd>
                      <dt>System OID</dt><dd className="mdd-snmp-oid">{snmp.sysObjectID || '\u2014'}</dd>
                      <dt>Uptime</dt><dd>{snmp.sysUpTimeStr || '\u2014'}</dd>
                      <dt>Contact</dt><dd>{snmp.sysContact || '\u2014'}</dd>
                      <dt>Location</dt><dd>{snmp.sysLocation || '\u2014'}</dd>
                      <dt>Classification</dt><dd>{snmp.classificationHint || '\u2014'}</dd>
                    </dl>
                    {snmp.interfaces && snmp.interfaces.length > 0 && (
                      <>
                        <h4 style={{ marginTop: '12px', marginBottom: '8px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                          Network Interfaces ({snmp.interfaceCount})
                        </h4>
                        <table className="mdd-snmp-iface-table">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Interface</th>
                              <th>Type</th>
                              <th>Speed</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {snmp.interfaces.map((iface, idx) => (
                              <tr key={idx}>
                                <td>{iface.index}</td>
                                <td>{iface.description}</td>
                                <td>{iface.typeName}</td>
                                <td>{iface.speed > 0 ? (iface.speed >= 1000000000 ? (iface.speed / 1000000000).toFixed(1) + ' Gbps' : (iface.speed / 1000000).toFixed(0) + ' Mbps') : '\u2014'}</td>
                                <td>
                                  <span className={'mdd-snmp-status ' + (iface.operStatus === 1 ? 'up' : 'down')}>
                                    {iface.operStatus === 1 ? 'Up' : 'Down'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {activeTab === 'hardware' && (() => {
          const bmc = device.discoveredDevice?.bmcData;
          if (!bmc?.supported) return <div className="mdd-info"><p>No BMC/Redfish data available.</p></div>;
          const healthColor = (s) => s === 'OK' ? '#00c853' : s === 'Warning' ? '#ffaa00' : s === 'Critical' ? '#ff1744' : '#78909c';
          const statusBadge = (s) => <span className="mdd-hw-status" style={{ color: healthColor(s), borderColor: healthColor(s) + '40', background: healthColor(s) + '15' }}>{s || '\u2014'}</span>;
          return (
            <div className="mdd-hardware">
              <div className="mdd-hw-section">
                <h3><Server size={16} /> System Overview</h3>
                <dl>
                  <dt>BMC Type</dt><dd>{bmc.bmcType || '\u2014'}</dd>
                  <dt>System Health</dt><dd>{statusBadge(bmc.systemHealth)}</dd>
                  <dt>Power State</dt><dd><span className={'mdd-hw-power ' + (bmc.powerState === 'On' ? 'on' : 'off')}>{bmc.powerState || '\u2014'}</span></dd>
                  <dt>Manufacturer</dt><dd>{bmc.manufacturer || '\u2014'}</dd>
                  <dt>Model</dt><dd>{bmc.model || '\u2014'}</dd>
                  <dt>Serial Number</dt><dd>{bmc.serialNumber || '\u2014'}</dd>
                  {bmc.serviceTag && <><dt>Service Tag</dt><dd>{bmc.serviceTag}</dd></>}
                  {bmc.sku && <><dt>SKU</dt><dd>{bmc.sku}</dd></>}
                  <dt>BIOS Version</dt><dd>{bmc.biosVersion || '\u2014'}</dd>
                  <dt>BMC Firmware</dt><dd>{bmc.firmwareVersion || '\u2014'}</dd>
                  {bmc.bmcModel && <><dt>BMC Model</dt><dd>{bmc.bmcModel}</dd></>}
                  <dt>Authenticated</dt><dd>{bmc.authenticated ? 'Yes' : 'No (limited data)'}</dd>
                </dl>
              </div>
              {bmc.cpus && bmc.cpus.length > 0 && (
                <div className="mdd-hw-section">
                  <h3><Cpu size={16} /> Processors ({bmc.cpus.length})</h3>
                  <table className="mdd-hw-table">
                    <thead><tr><th>Socket</th><th>Model</th><th>Cores</th><th>Threads</th><th>Speed</th><th>Status</th></tr></thead>
                    <tbody>
                      {bmc.cpus.map((cpu, i) => (
                        <tr key={i}>
                          <td>{cpu.socket || cpu.name || 'CPU ' + i}</td>
                          <td>{cpu.model || '\u2014'}</td>
                          <td>{cpu.cores || '\u2014'}</td>
                          <td>{cpu.threads || '\u2014'}</td>
                          <td>{cpu.speedMHz ? cpu.speedMHz + ' MHz' : '\u2014'}</td>
                          <td>{statusBadge(cpu.status)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {(bmc.totalMemoryGB > 0 || (bmc.memoryDIMMs && bmc.memoryDIMMs.length > 0)) && (
                <div className="mdd-hw-section">
                  <h3><HardDriveIcon size={16} /> Memory {bmc.totalMemoryGB ? '(' + bmc.totalMemoryGB + ' GB Total)' : ''}</h3>
                  {bmc.memoryDIMMs && bmc.memoryDIMMs.length > 0 && (
                    <table className="mdd-hw-table">
                      <thead><tr><th>Slot</th><th>Size</th><th>Speed</th><th>Type</th><th>Status</th></tr></thead>
                      <tbody>
                        {bmc.memoryDIMMs.map((dimm, i) => (
                          <tr key={i}>
                            <td>{dimm.slot || dimm.name || 'DIMM ' + i}</td>
                            <td>{dimm.sizeGB ? dimm.sizeGB.toFixed(0) + ' GB' : '\u2014'}</td>
                            <td>{dimm.speedMHz ? dimm.speedMHz + ' MHz' : '\u2014'}</td>
                            <td>{dimm.type || '\u2014'}</td>
                            <td>{statusBadge(dimm.status)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
              {bmc.storageControllers && bmc.storageControllers.length > 0 && (
                <div className="mdd-hw-section">
                  <h3><Database size={16} /> Storage</h3>
                  {bmc.storageControllers.map((ctrl, ci) => (
                    <div key={ci} className="mdd-hw-storage-ctrl">
                      <div className="mdd-hw-ctrl-header">
                        <strong>{ctrl.name || 'Controller ' + ci}</strong>
                        {ctrl.model && <span className="mdd-hw-ctrl-model">{ctrl.model}</span>}
                        {ctrl.firmwareVersion && <span className="mdd-hw-ctrl-fw">FW: {ctrl.firmwareVersion}</span>}
                        {statusBadge(ctrl.status)}
                      </div>
                      {ctrl.virtualDrives && ctrl.virtualDrives.length > 0 && (<>
                        <h4>Virtual Drives</h4>
                        <table className="mdd-hw-table">
                          <thead><tr><th>Name</th><th>RAID</th><th>Capacity</th><th>Status</th></tr></thead>
                          <tbody>{ctrl.virtualDrives.map((vd, vi) => (
                            <tr key={vi}><td>{vd.name || 'VD ' + vi}</td><td>{vd.raidLevel || '\u2014'}</td><td>{vd.capacityGB ? vd.capacityGB.toFixed(0) + ' GB' : '\u2014'}</td><td>{statusBadge(vd.status)}</td></tr>
                          ))}</tbody>
                        </table>
                      </>)}
                      {ctrl.physicalDrives && ctrl.physicalDrives.length > 0 && (<>
                        <h4>Physical Drives ({ctrl.physicalDrives.length})</h4>
                        <table className="mdd-hw-table">
                          <thead><tr><th>Name</th><th>Capacity</th><th>Type</th><th>Protocol</th><th>Model</th><th>Status</th></tr></thead>
                          <tbody>{ctrl.physicalDrives.map((d, di) => (
                            <tr key={di}><td>{d.name || 'Drive ' + di}</td><td>{d.capacityGB ? d.capacityGB.toFixed(0) + ' GB' : '\u2014'}</td><td>{d.mediaType || '\u2014'}</td><td>{d.protocol || '\u2014'}</td><td>{d.model || '\u2014'}</td><td>{statusBadge(d.status)}</td></tr>
                          ))}</tbody>
                        </table>
                      </>)}
                    </div>
                  ))}
                </div>
              )}
              {bmc.powerSupplies && bmc.powerSupplies.length > 0 && (
                <div className="mdd-hw-section">
                  <h3><Zap size={16} /> Power {bmc.powerConsumedWatts ? '(' + bmc.powerConsumedWatts.toFixed(0) + 'W consumed)' : ''}</h3>
                  {bmc.powerRedundancy && <div className="mdd-hw-redundancy">Redundancy: {statusBadge(bmc.powerRedundancy)}</div>}
                  <table className="mdd-hw-table">
                    <thead><tr><th>PSU</th><th>Capacity</th><th>Model</th><th>Serial</th><th>Status</th></tr></thead>
                    <tbody>{bmc.powerSupplies.map((psu, i) => (
                      <tr key={i}><td>{psu.name || 'PSU ' + i}</td><td>{psu.powerWatts ? psu.powerWatts + 'W' : '\u2014'}</td><td>{psu.model || '\u2014'}</td><td>{psu.serialNumber || '\u2014'}</td><td>{statusBadge(psu.status)}</td></tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
              {bmc.fans && bmc.fans.length > 0 && (
                <div className="mdd-hw-section">
                  <h3><RefreshCw size={16} /> Fans ({bmc.fans.length})</h3>
                  <div className="mdd-hw-fans-grid">
                    {bmc.fans.map((fan, i) => (
                      <div key={i} className="mdd-hw-fan-card">
                        <div className="mdd-hw-fan-name">{fan.name || 'Fan ' + i}</div>
                        <div className="mdd-hw-fan-rpm">{fan.rpm ? fan.rpm.toLocaleString() + ' RPM' : '\u2014'}</div>
                        {statusBadge(fan.status)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {bmc.temperatures && bmc.temperatures.length > 0 && (
                <div className="mdd-hw-section">
                  <h3><Activity size={16} /> Temperatures</h3>
                  <table className="mdd-hw-table">
                    <thead><tr><th>Sensor</th><th>Reading</th><th>Threshold</th><th>Status</th></tr></thead>
                    <tbody>{bmc.temperatures.map((t, i) => (
                      <tr key={i}><td>{t.name}</td><td className={'mdd-hw-temp ' + (t.readingCelsius > (t.upperThresholdCritical || 90) ? 'critical' : t.readingCelsius > 70 ? 'warn' : 'ok')}>{t.readingCelsius ? t.readingCelsius.toFixed(0) + '°C' : '\u2014'}</td><td>{t.upperThresholdCritical ? t.upperThresholdCritical + '°C' : '\u2014'}</td><td>{statusBadge(t.status)}</td></tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
              {((bmc.managementIPs && bmc.managementIPs.length > 0) || (bmc.managementMACs && bmc.managementMACs.length > 0)) && (
                <div className="mdd-hw-section">
                  <h3><Wifi size={16} /> Management Network</h3>
                  <dl>
                    {bmc.managementIPs && bmc.managementIPs.map((ip, i) => <><dt key={'ip'+i}>Management IP</dt><dd>{ip}</dd></>)}
                    {bmc.managementMACs && bmc.managementMACs.map((mac, i) => <><dt key={'mac'+i}>Management MAC</dt><dd>{mac}</dd></>)}
                  </dl>
                </div>
              )}
              {bmc.recentEvents && bmc.recentEvents.length > 0 && (
                <div className="mdd-hw-section">
                  <h3><AlertTriangle size={16} /> Recent Events ({bmc.recentEvents.length})</h3>
                  <table className="mdd-hw-table mdd-hw-events">
                    <thead><tr><th>Time</th><th>Severity</th><th>Message</th></tr></thead>
                    <tbody>{bmc.recentEvents.map((evt, i) => (
                      <tr key={i}><td className="mdd-hw-evt-time">{evt.timestamp ? new Date(evt.timestamp).toLocaleString() : '\u2014'}</td><td>{statusBadge(evt.severity)}</td><td>{evt.message || '\u2014'}</td></tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })()}

        {activeTab === 'inventory' && (
          <div className="mdd-inventory">
            <div className="mdd-inventory-toolbar">
              <button className="btn btn-secondary btn-sm" onClick={handleRefreshInventory} disabled={inventoryRefreshing}>
                <RefreshCw size={14} className={inventoryRefreshing ? 'spin' : ''} /> Refresh Inventory
              </button>
              {osInventory?.collectedAt && (
                <span className="mdd-inventory-collected">Last collected: {new Date(osInventory.collectedAt).toLocaleString()}</span>
              )}
            </div>

            <div className="mdd-hw-section">
              <h3><Server size={16} /> Hardware</h3>
              {!osInventory && osInventoryError ? (
                <p className="mdd-no-data">{osInventoryError}</p>
              ) : (
                <dl>
                  <dt>Manufacturer</dt><dd>{osInventory?.hardware?.manufacturer || device.manufacturer || '—'}</dd>
                  <dt>Model</dt><dd>{osInventory?.hardware?.model || device.model || '—'}</dd>
                  <dt>Serial Number</dt><dd>{osInventory?.hardware?.serialNumber || '—'}</dd>
                  <dt>OS Version</dt><dd>{osInventory?.hardware?.osVersion || '—'}</dd>
                  <dt>OS Build</dt><dd>{osInventory?.hardware?.osBuild || '—'}</dd>
                </dl>
              )}
            </div>

            <div className="mdd-stats-row">
              <div className="mdd-stat-card">
                <div className="mdd-stat-icon" style={{ color: '#00d4aa' }}><Cpu size={18} /></div>
                <div className="mdd-stat-body">
                  <span className="mdd-stat-value">{latestCpu != null ? latestCpu.toFixed(0) : '—'}<small>%</small></span>
                  <span className="mdd-stat-label">CPU</span>
                </div>
              </div>
              <div className="mdd-stat-card">
                <div className="mdd-stat-icon" style={{ color: '#448aff' }}><MemoryStick size={18} /></div>
                <div className="mdd-stat-body">
                  <span className="mdd-stat-value">{latestMem != null ? latestMem.toFixed(0) : '—'}<small>%</small></span>
                  <span className="mdd-stat-label">Memory</span>
                </div>
              </div>
              <div className="mdd-stat-card">
                <div className="mdd-stat-icon" style={{ color: '#ffaa00' }}><HardDriveIcon size={18} /></div>
                <div className="mdd-stat-body">
                  <span className="mdd-stat-value">{latestDiskMax != null ? latestDiskMax.toFixed(0) : '—'}<small>%</small></span>
                  <span className="mdd-stat-label">Disk (max)</span>
                </div>
              </div>
            </div>

            <div className="mdd-chart-card">
              <div className="mdd-chart-header"><h3>CPU &amp; Memory (24h)</h3></div>
              <div className="mdd-chart-body">
                {osHealthChartData.length > 1 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={osHealthChartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="cpuGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#00d4aa" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#00d4aa" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="memGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#448aff" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#448aff" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#2a2f3a" />
                      <XAxis dataKey="time" type="number" domain={['dataMin', 'dataMax']}
                        tickFormatter={(t) => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        stroke="#666" tick={{ fontSize: 11 }} />
                      <YAxis stroke="#666" tick={{ fontSize: 11 }} unit="%" domain={[0, 100]} />
                      <Tooltip contentStyle={{ background: '#1a1f2e', border: '1px solid #333', borderRadius: 8 }}
                        labelFormatter={(t) => new Date(t).toLocaleString()}
                        formatter={(v, name) => [v.toFixed(1) + '%', name === 'cpu' ? 'CPU' : 'Memory']} />
                      <Area type="monotone" dataKey="cpu" stroke="#00d4aa" fill="url(#cpuGrad)" strokeWidth={2} dot={false} />
                      <Area type="monotone" dataKey="memory" stroke="#448aff" fill="url(#memGrad)" strokeWidth={2} dot={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="mdd-no-data">No OS health data yet</div>
                )}
              </div>
            </div>

            <div className="mdd-hw-section">
              <h3><Activity size={16} /> Services ({osInventory?.allServices?.length || 0})</h3>
              {!osInventory?.allServices?.length ? (
                <p className="mdd-no-data">No service list collected yet</p>
              ) : (
                <table className="mdd-hw-table">
                  <thead>
                    <tr><th>Name</th><th>Display Name</th><th>Status</th><th>Start Mode</th><th>Watched</th><th></th></tr>
                  </thead>
                  <tbody>
                    {osInventory.allServices.map((svc) => {
                      const watch = findWatch(svc.name);
                      return (
                        <tr key={svc.name}>
                          <td>{svc.name}</td>
                          <td>{svc.displayName || '—'}</td>
                          <td><span className="mdd-hw-status" style={svcStatusStyle(svc.status)}>{svc.status}</span></td>
                          <td>{svc.startMode || '—'}</td>
                          <td>
                            {watch ? (
                              <span className="mdd-hw-status" style={svcStatusStyle(watch.lastStatus)}>
                                {watch.lastStatus || 'pending'}
                              </span>
                            ) : '—'}
                          </td>
                          <td>
                            <button
                              className={'mdd-watch-btn' + (watch ? ' watching' : '')}
                              disabled={watchBusyName === svc.name}
                              onClick={() => handleToggleWatch(svc)}
                            >
                              {watch ? <><EyeOff size={12} /> Unwatch</> : <><Eye size={12} /> Watch</>}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            <div className="mdd-hw-section">
              <h3><Database size={16} /> Installed Software ({filteredSoftware.length})</h3>
              <div className="mdd-search">
                <Search size={14} />
                <input placeholder="Search by name or publisher..." value={softwareSearch} onChange={(e) => setSoftwareSearch(e.target.value)} />
              </div>
              {!osInventory?.software?.length ? (
                <p className="mdd-no-data">No software list collected yet</p>
              ) : (
                <table className="mdd-hw-table">
                  <thead><tr><th>Name</th><th>Version</th><th>Publisher</th><th>Install Date</th></tr></thead>
                  <tbody>
                    {filteredSoftware.map((sw, i) => (
                      <tr key={i}>
                        <td>{sw.name}</td>
                        <td>{sw.version || '—'}</td>
                        <td>{sw.publisher || '—'}</td>
                        <td>{sw.installDate || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

      </div>

      {/* Live View Modal */}
      {showLiveModal && (
        <div className="mdd-live-overlay" onClick={handleCloseLiveView}>
          <div className="mdd-live-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mdd-live-header">
              <h3><Play size={16} /> {device.name} — Live</h3>
              <button className="mdd-live-close" onClick={handleCloseLiveView}>
                <XCircle size={20} />
              </button>
            </div>
            <div className="mdd-live-player">
              {device.source === 'INTEGRATION' && device.externalId ? (
                <WebRTCPlayer
                  camera={{ id: device.externalId, name: device.name }}
                  stream="secondary"
                  muted={true}
                />
              ) : streamLoading ? (
                <div className="mdd-stream-status"><RefreshCw size={24} className="spin" /><span>Connecting to stream...</span></div>
              ) : streamError ? (
                <div className="mdd-stream-status mdd-stream-error"><XCircle size={24} /><span>{streamError}</span></div>
              ) : janusStreamId ? (
                <JanusPlayer
                  streamId={janusStreamId}
                  cameraName={device.name}
                  autoPlay={true}
                  muted={true}
                />
              ) : (
                <div className="mdd-stream-status"><Video size={24} /><span>No stream available</span></div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
